import React from 'react'
import { Input } from '@/components/ui/input'
import { BaseFormFieldProps } from '@/types/form'
import { FormLabel } from '@/components/ui/forms'
import { cn } from '@/utilities/ui'

type FormInputAriaProps = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  | 'role'
  | 'aria-expanded'
  | 'aria-controls'
  | 'aria-activedescendant'
  | 'aria-autocomplete'
  | 'aria-busy'
  | 'aria-label'
>

interface FormInputProps extends BaseFormFieldProps, FormInputAriaProps {
  id: string
  label: string
  type?: 'text' | 'email' | 'tel' | 'password' | 'number'
  inputClassName?: string
  testId?: string
  dataTestId?: string
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  helperText?: string
  errorText?: string
  disabled?: boolean
  readOnly?: boolean
  autoComplete?: string
  autoFocus?: boolean
  maxLength?: number
  minLength?: number
  pattern?: string
  numbersOnly?: boolean
  // Styling props
  variant?: 'outlined' | 'filled' | 'standard'
  size?: 'small' | 'medium' | 'large'
  fullWidth?: boolean
  // Event handlers
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  disableFocusRing?: boolean
}

export function FormInput({
  id,
  label,
  type = 'text',
  value,
  placeholder,
  required = false,
  showInfoIcon = false,
  error,
  className = '',
  inputClassName = '',
  labelClassName = '',
  testId,
  dataTestId,
  startIcon,
  endIcon,
  helperText,
  errorText,
  disabled = false,
  readOnly = false,
  autoComplete,
  autoFocus = false,
  maxLength,
  minLength,
  pattern,
  numbersOnly = false,
  variant = 'outlined',
  size = 'medium',
  fullWidth = false,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onKeyUp,
  disableFocusRing = false,
  role,
  'aria-expanded': ariaExpanded,
  'aria-controls': ariaControls,
  'aria-activedescendant': ariaActivedescendant,
  'aria-autocomplete': ariaAutocomplete,
  'aria-busy': ariaBusy,
  'aria-label': ariaLabel,
}: Readonly<FormInputProps>) {
  const resolvedTestId = dataTestId ?? testId
  const hasError = Boolean(error || errorText)

  // Handle key down for numbers only
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (numbersOnly) {
      // Allow: backspace, delete, tab, escape, enter, and navigation keys
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
        'PageUp',
        'PageDown',
      ]

      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (event.ctrlKey && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) {
        return
      }

      // Allow numbers 0-9
      if (/^[0-9]$/.test(event.key)) {
        return
      }

      // Block all other keys
      if (!allowedKeys.includes(event.key)) {
        event.preventDefault()
        return
      }
    }

    // Call the original onKeyDown if provided
    if (onKeyDown) {
      onKeyDown(event)
    }
  }

  // Base classes
  const baseInputClasses = [
    'w-full px-3 py-2 border rounded-md transition-colors duration-200',
    disableFocusRing
      ? 'outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0'
      : 'focus:outline-none focus:ring-2 focus:ring-offset-0',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'readonly:bg-gray-50',
    'placeholder:text-tenant-description-text',
  ]

  // Variant classes
  const variantClasses = {
    outlined: disableFocusRing
      ? 'border-gray-300'
      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
    filled: disableFocusRing
      ? 'border-transparent bg-gray-100 focus:bg-white'
      : 'border-transparent bg-gray-100 focus:bg-white focus:border-blue-500 focus:ring-blue-500',
    standard: disableFocusRing
      ? 'border-b-2 border-gray-300 rounded-none'
      : 'border-b-2 border-gray-300 rounded-none focus:border-blue-500',
  }

  // Size classes
  const sizeClasses = {
    small: 'text-sm py-1.5',
    medium: 'text-base py-2',
    large: 'text-lg py-3',
  }

  // Error classes
  const errorClasses = hasError
    ? 'border-error-primary focus:border-error-primary focus:ring-error-primary'
    : ''

  // Width classes
  const widthClasses = fullWidth ? 'w-full' : ''

  // Icon padding classes
  const iconPaddingClasses = [startIcon ? 'pl-10' : '', endIcon ? 'pr-10' : '']
    .filter(Boolean)
    .join(' ')

  const inputClasses = [
    ...baseInputClasses,
    variantClasses[variant],
    sizeClasses[size],
    errorClasses,
    widthClasses,
    iconPaddingClasses,
    inputClassName,
  ].join(' ')

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${className}`} data-testid="form-input">
      {label && (
        <FormLabel
          htmlFor={id}
          className={cn('block text-sm mb-1', labelClassName)}
          required={required}
          showInfoIcon={showInfoIcon}
        >
          {label}
        </FormLabel>
      )}

      <div className="relative">
        {startIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            {startIcon}
          </div>
        )}

        <Input
          id={id}
          data-testid={resolvedTestId}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={inputClasses}
          disabled={disabled}
          readOnly={readOnly}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          maxLength={maxLength}
          minLength={minLength}
          {...(pattern ? { pattern } : {})}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={onKeyUp}
          role={role}
          aria-expanded={ariaExpanded}
          aria-controls={ariaControls}
          aria-activedescendant={ariaActivedescendant}
          aria-autocomplete={ariaAutocomplete}
          aria-busy={ariaBusy}
          aria-label={ariaLabel}
        />

        {endIcon && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            {endIcon}
          </div>
        )}
      </div>

      {/* Helper text or error message — height follows the text so one-line errors stay compact */}
      <div className={helperText || errorText || error ? 'mt-0.5' : 'h-0'}>
        {helperText || errorText || error ? (
          <p
            className={cn(
              'text-xs leading-4 whitespace-pre-wrap',
              hasError ? 'text-error-primary' : 'text-gray-500',
            )}
            role={hasError ? 'alert' : undefined}
          >
            {errorText || error || helperText}
          </p>
        ) : null}
      </div>
    </div>
  )
}
